<script lang="ts" setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { getErrorCode } from '@/app/core/composable/general'
import { useI18n } from 'vue-i18n'
import { DBtn, DTextField, DTable, DLoading, DModal } from 'front-library'
import {
  DeleteRecurringIncidentRequests,
  getDeleteRecurringIncidentRequests
} from '@/app/core/services/recurring-incidents'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const listErrors = ref()

const loading = ref(false)

/* Datos */
const search = ref()

/* Datos componente table */
const columns = ref([
  { name: 'name_user', title: t('recurringIncidents.delete.user'), table: 'name_user' },
  {
    name: 'observation',
    title: t('recurringIncidents.delete.observations'),
    table: 'observation'
  },
  { name: 'status', title: t('recurringIncidents.delete.status'), table: 'status_name' },
  {
    name: 'successful_rows',
    title: t('recurringIncidents.delete.summary'),
    table: 'successful_rows'
  },
  {
    name: 'summary',
    title: t('recurringIncidents.delete.summaryDetail'),
    table: 'summary'
  },
  { name: 'list_errors', title: t('recurringIncidents.delete.errors'), table: 'errors' },
  {
    name: 'updated_at',
    title: t('recurringIncidents.delete.lastUpdate'),
    table: 'updated_at'
  }
])
const rows = ref<DeleteRecurringIncidentRequests[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createDeleteRecurringIncidentRequests = () => {
  void router.push({ name: 'DeleteRecurringIncident' })
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
}

/* Listar solicitudes de eliminacion de incidencias recurrentes */
const recurringIncidentDeletionRequests = async () => {
  loading.value = true
  try {
    const data: any = await getDeleteRecurringIncidentRequests(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    console.log('incidencias recurrentes',data)
    rows.value = data.results
    totalRows.value = data.totalItems
    loading.value = false
  } catch (error: any) {
    loading.value = false
    const code = getErrorCode(error.response.data?.code)
    notifications.addNotification({
      title: t('error.titles.' + code),
      text: t('error.messages.' + code),
      type: 'error',
      time: 10000
    })
  }
}
</script>
<template>
  <DLoading v-show="loading" :message="t('loading.data')" />
  <DModal
    :open="showModal"
    :title="t('recurringIncidents.delete.listErrors')"
    @closeModal="closeModal">
    <template v-slot:description>
      <div class="text-left" style="max-height: calc(100vh - 15rem)">
        <div
          v-for="(error, index) in listErrors"
          :key="index"
          class="text-gray-600">
          <span class="text-red-700">&#10006;</span> {{ error }}
        </div>
      </div>
    </template>
  </DModal>
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('recurringIncidents.delete.masiveTitle', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('recurringIncidents.delete.masiveDescription')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="recurringIncidentDeletionRequests" />
          <DBtn
            @click.prevent="recurringIncidentDeletionRequests"
            v-html="t('form.button.search')" />
        </div>
        <DBtn
          :icon="'PlusIcon'"
          type="button"
          @click="createDeleteRecurringIncidentRequests()"
          v-if="store.hasPermission('delete_recurring_incident_request:bulk')">
          {{
            t('form.button.create', {
              form: t('recurringIncidents.delete.masiveTitle', 0)
            })
          }}
        </DBtn>
      </div>
    </div>
    <DTable
      ref="tableDeleteRecurringIncidentRequests"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), recurringIncidentDeletionRequests()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), recurringIncidentDeletionRequests()">
      <template #amount_incidents="data: any">
        <div>
          {{ data.incidents.length }}
        </div>
      </template>
      <template #summary="data: any">
        <div>
          {{ Array.isArray(data.summary) ? data.summary.join('; ') : data.summary }}
        </div>
      </template>
      <template #list_errors="data: any">
        <div class="my-1.5">
          <div v-if="data.errors.length === 1">
            <p class="text-sm text-gray-500">
              <span class="text-red-700">&#10006;</span> {{ data.errors[0] }}
            </p>
          </div>
          <div
            v-if="data.errors.length > 1"
            class="bg-red-50 rounded-lg text-red-700 text-sm font-semibold py-1 px-2 mt-1.5 w-max cursor-pointer"
            @click=";(showModal = true), (listErrors = data.errors)">
            <p>
              {{
                t('recurringIncidents.delete.showErrors', { cant: data.errors.length })
              }}
            </p>
          </div>
        </div>
      </template>
      <template #status="data: any">
        <div
          class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3"
          :class="
            data.status_code === 'NOSTART'
              ? 'bg-gray-100 text-gray-600'
              : data.status_code === 'PENDING'
              ? 'bg-blue-100 text-blue-600'
              : data.status_code === 'ERROR'
              ? 'bg-red-100 text-red-600'
              : 'bg-green-100 text-green-600'
          ">
          <div
            class="mr-2 w-1.5 h-1.5 rounded-full"
            :class="
              data.status_code === 'NOSTART'
                ? 'bg-gray-600'
                : data.status_code === 'PENDING'
                ? 'bg-blue-600'
                : data.status_code === 'ERROR'
                ? 'bg-red-600'
                : 'bg-green-600'
            "></div>
          {{ data.status_name }}
        </div>
      </template>
    </DTable>
  </div>
</template>
